<template>
  <zm-context :system-fonts="false" :namespace="NAME_SPACE">
    <footer
      ref="footerRef"
      :class="['footer-copyright', { 'is-minimalist': isMinimalist }]"
      :style="[footerStyle, footerMarginStyle]">
      <span class="footer-copyright-left">
        <span class="footer-copyright-text">
          {{ copyrightText || $t('copyright', [currentYear]) }}
        </span>

        <p v-if="!isMinimalist" class="legal-links-wrapper">
          <a
            v-for="link in resourcesLinks"
            :key="link.url"
            :href="link.url"
            :target="link.openIn ?? '_blank'"
            rel="noopener noreferrer">
            {{ link.label }}
          </a>
          <a
            v-for="link in legalLinks"
            :key="link.url"
            :href="link.url"
            :target="link.openIn ?? '_blank'"
            rel="noopener noreferrer">
            {{ link.label }}
          </a>

          <template v-if="showOneTrustLinks && !isZoomGovSite">
            <button
              id="ot-do-not-sell"
              class="ot-sdk-show-settings"
              type="button"
              @keydown.enter.prevent.stop="handleOneTrustKeyActivate"
              @keydown.space.prevent.stop="handleOneTrustKeyActivate">
              {{ doNotSellLabel || $t('do_not_sell_my_personal_information') }}
            </button>
            <button
              id="ot-cookie-pref"
              class="ot-sdk-show-settings"
              type="button"
              @keydown.enter.prevent.stop="handleOneTrustKeyActivate"
              @keydown.space.prevent.stop="handleOneTrustKeyActivate">
              {{ cookiePreferencesLabel || $t('cookie_preferences') }}
            </button>
          </template>
          <template v-else-if="showTrustFlag && !isZoomGovSite">
            <a id="trust-flag" class="footer-link-hidden" aria-hidden="true"></a>
          </template>
          <a id="teconsent" class="footer-link-hidden" aria-hidden="true"></a>

          <template v-if="config.isDefault">
            <a v-if="!isZoomGovSite" target="_blank" :href="pricingLink?.url || '/pricing'">
              {{ pricingLink?.label || $t('pricing') }}
            </a>
            <a
              v-if="isFeedbackEnabled"
              id="feedback-link"
              class="feedback-show-dialog"
              role="button"
              tabindex="0"
              @keydown.enter.prevent="onFeedback"
              @keydown.space.prevent="onFeedback"
              @click="onFeedback">
              {{ feedbackLabel || $t('feedback') }}
            </a>
            <a :href="contactUsUrl" target="_blank" rel="noopener noreferrer">
              {{ contactLink?.label || $t('contact_us') }}
            </a>
          </template>
          <template v-else>
            <a
              v-for="link in supportsLinks"
              :key="link.url"
              :role="link.id === 'feedback' ? 'button' : 'link'"
              tabindex="0"
              @keydown.enter.prevent="onSupportLinkClick(link)"
              @keydown.space.prevent="onSupportLinkClick(link)"
              @click="onSupportLinkClick(link)">
              {{ link.label }}
            </a>
          </template>
        </p>
      </span>

      <span v-if="!isZoomGovSite" class="footer-copyright-right">
        <zm-dropdown :z-index="300" @command="handleCommand">
          <zm-button
            type="tertiary"
            class="footer-language"
            :icon="ChevronDownV2"
            icon-reverse
            :aria-label="languageAriaLabel">
            {{ currentLanguage }}
          </zm-button>
          <template #dropdown>
            <zm-dropdown-item v-for="item in localeOptions" :key="item.key" :command="item.key">
              {{ item.value }}
            </zm-dropdown-item>
          </template>
        </zm-dropdown>
      </span>
    </footer>
  </zm-context>
</template>

<script setup>
import { ref, computed, onBeforeUnmount, onMounted, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { ChevronDownV2 } from '@zoom/icons-vue'
import { ZmDropdown, ZmButton, ZmDropdownItem, ZmContext } from '@zoom/zoom-ui-vue3'
import { NAME_SPACE } from '@/commons/constants'
import { useFooterConfig } from './composables/use-footer-config'
import { useLanguageSwitcher } from './composables/use-language-switcher'
import { SELECTORS } from './constants'
import { withAlpha } from './style-helpers'

defineOptions({ name: 'frame-footer-copyright' })
const props = defineProps({
  config: {
    type: Object,
    default: () => ({})
  },
  isMinimalist: Boolean
})
const CONTACT_US_URL = 'https://explore.zoom.us/en/contact'
const DEFAULT_TEXT_COLOR = '#666487'
const LOAD_SYNC_DELAY_MS = 1000

const footerRef = useTemplateRef('footerRef')

const {
  resourcesLinks,
  legalLinks,
  showOneTrustLinks,
  showTrustFlag,
  supportsLinks,
  copyrightText,
  pricingLink,
  feedbackLabel,
  contactLink,
  doNotSellLabel,
  cookiePreferencesLabel
} = useFooterConfig(props.config)

const { t } = useI18n()
const { currentLanguage, localeOptions, handleCommand } = useLanguageSwitcher()
const isZoomGovSite = computed(() => window.microsDatas.isZoomGovSite ?? false)
const isFeedbackEnabled = computed(() => Boolean(window.microsDatas.feedback))
const currentYear = computed(() => new Date().getFullYear())
const languageAriaLabel = computed(() => t('aria.language', [currentLanguage.value]))
const contactUsUrl = computed(() => contactLink.value?.url || CONTACT_US_URL)

const footerStyle = computed(() => {
  if (props.isMinimalist) return null
  const style = props.config?.style ?? {}
  return {
    backgroundColor: withAlpha(style.backgroundColor, style.backgroundOpacity),
    color: withAlpha(style.textColor || DEFAULT_TEXT_COLOR, style.textOpacity)
  }
})

const onSupportLinkClick = (link) => {
  if (link.id === 'feedback') {
    window.feedback?.showDialog?.()
  } else {
    window.open(link.url, link.openIn ?? '_blank')
  }
}
const onFeedback = () => {
  window.feedback?.showDialog?.()
}

const handleOneTrustKeyActivate = (event) => {
  event.currentTarget?.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }))
}

const containerPaddingLeft = ref(0)
const containerPaddingRight = ref(0)

const footerMarginStyle = computed(() =>
  props.isMinimalist
    ? null
    : {
        marginLeft: `-${containerPaddingLeft.value}px`,
        marginRight: `-${containerPaddingRight.value}px`
      }
)

let resizeHandler = null
let loadHandler = null

onMounted(() => {
  const containerEl = document.querySelector(SELECTORS.CONTENT_BODY)
  if (!containerEl) return

  const syncPadding = () => {
    const style = window.getComputedStyle(containerEl)
    if (!style) return
    containerPaddingLeft.value = parseFloat(style.paddingLeft) || 0
    containerPaddingRight.value = parseFloat(style.paddingRight) || 0
  }

  syncPadding()

  if (document.readyState !== 'complete') {
    loadHandler = () => setTimeout(syncPadding, LOAD_SYNC_DELAY_MS)
    window.addEventListener('load', loadHandler, { once: true })
  }

  resizeHandler = () => syncPadding()
  window.addEventListener('resize', resizeHandler)
})

onBeforeUnmount(() => {
  if (resizeHandler) {
    window.removeEventListener('resize', resizeHandler)
    resizeHandler = null
  }
  if (loadHandler) {
    window.removeEventListener('load', loadHandler)
    loadHandler = null
  }
})
</script>

<style lang="scss" scoped>
:global(.vanity-site .footer-language) {
  color: inherit;
  &:hover {
    color: inherit !important;
  }
}
:global(.vanity-site .footer-copyright .legal-links-wrapper a:not(:last-child)::after) {
  background-color: inherit;
}
.footer-copyright {
  display: flex;
  text-align: center;
  justify-content: space-between;
  align-items: center;
  padding: 24px 32px;
  border-top: 1px solid var(--zoom-color-border-subtle-neutral);

  &.is-minimalist {
    justify-content: center;
    border-top: none;
    .footer-copyright-right {
      margin-right: 0;
    }
  }

  .footer-copyright-text {
    color: inherit;
    font-size: 12px;
    text-align: left;
  }

  .footer-copyright-left {
    display: flex;
    flex-direction: column;
    align-items: baseline;
    margin-right: 24px;
    a {
      color: inherit;
    }

    button.ot-sdk-show-settings {
      color: inherit;
      background: none;
      border: 0;
      padding: 0;
      font: inherit;
      line-height: inherit;
      cursor: pointer;
    }
  }
  .footer-copyright-right {
    margin-right: 40px;
  }

  .legal-links-wrapper {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-start;
    column-gap: 16px;
    row-gap: 6px;
    font-size: 12px;
    margin: 8px 0 0;
    a,
    button.ot-sdk-show-settings {
      position: relative;
      cursor: pointer;

      &:not(:last-child)::after {
        content: '';
        position: absolute;
        top: 50%;
        right: -8px;
        width: 1px;
        height: 12px;
        transform: translateY(-50%);
        background-color: var(--zoom-color-border-subtle-neutral, #dfe3e8);
      }
    }
  }

  .footer-link-hidden {
    display: none;
  }

  .footer-language {
    margin-left: -8px;
  }
  #ot-do-not-sell {
    padding-left: 38px;
  }
  #ot-do-not-sell::before {
    left: 0;
  }
}

@media screen and (max-width: 1023px) {
  .footer-copyright {
    display: block;
    text-align: left;
    padding-left: 16px;
    padding-right: 16px;

    .footer-copyright-left p {
      text-align: left;
      flex-wrap: wrap;
      justify-content: flex-start;
      row-gap: 6px;
    }

    .footer-copyright-right {
      margin-top: 8px;
      display: block;
      margin-right: 0;
    }
  }
}
</style>
